import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { GridColDef } from '@mui/x-data-grid-premium';
import CloseIcon from '@mui/icons-material/Close';
import { useLocalization } from '@/hooks/useLocalization';
import type { AdvisorAggregatedHoldingHeldBy } from '@/store/advisorAggregatedHoldingsApi';
import { PAGE_SIZES, PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { GridContainer } from '@/components/grids/GridContainer';

export interface HeldByModalProps {
  open: boolean;
  onClose: () => void;
  holders: AdvisorAggregatedHoldingHeldBy[];
  holdingName: string;
}

const HeldByModal = ({
  open,
  onClose,
  holders,
  holdingName,
}: HeldByModalProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const columns = useMemo<GridColDef<AdvisorAggregatedHoldingHeldBy>[]>(
    () => [
      {
        field: 'InvestorName',
        headerName: t('client', 'Client'),
        flex: 1,
        minWidth: 200,
      },
    ],
    [t],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      fullScreen={isXs}
      sx={(dialogTheme) => ({
        [dialogTheme.breakpoints.down('sm')]: {
          '& .MuiPaper-root': {
            paddingTop:
              'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
          },
        },
      })}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {t('heldByModal.title', 'Held By: {{holdingName}}', { holdingName })}
        <IconButton
          onClick={onClose}
          aria-label={t('close', 'Close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <GridContainer
          isWidget={false}
          size='flyout'
        >
          <DataGridPremium<AdvisorAggregatedHoldingHeldBy>
            rows={holders}
            columns={columns}
            getRowId={(row) => row.InvestorId}
            pagination
            hideFooter={holders.length <= PAGE_SIZES.SMALL}
            initialState={{
              pagination: {
                paginationModel: { pageSize: PAGE_SIZES.SMALL },
              },
              sorting: {
                sortModel: [{ field: 'InvestorName', sort: 'asc' }],
              },
            }}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS.MODAL]}
            disableColumnMenu
            disableRowSelectionOnClick
          />
        </GridContainer>
      </DialogContent>
    </Dialog>
  );
};

export default HeldByModal;
